import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

const faqItems = [
  {
    q: 'What is Opticvance?',
    a: 'Opticvance is an AI-powered analytical intelligence platform that transforms complex, multi-source enterprise data into predictive insights and actionable recommendations in real time.',
  },
  {
    q: 'Who can benefit from Opticvance?',
    a: 'Enterprise decision-makers, business analysts, operations managers, strategy teams, and SMEs looking to leverage predictive intelligence for smarter decision-making can benefit from our platform.',
    bullets: ['Enterprise decision-makers', 'Business analysts', 'Operations managers', 'Strategy teams', 'SMEs'],
  },
  {
    q: 'How does Opticvance handle large datasets?',
    a: 'The platform uses GPU-accelerated computation to process petabyte-scale, multi-source datasets with sub-second inference latency, ensuring timely and accurate analytics.',
    bullets: ['GPU-accelerated computation', 'Petabyte-scale ingestion', 'Sub-second inference latency', 'Optimized distributed pipelines'],
  },
  {
    q: 'Can Opticvance integrate with existing systems?',
    a: 'Yes. Opticvance provides API integrations and supports multiple data sources, including ERP, CRM, cloud storage, and IoT systems, allowing seamless incorporation into your workflows.',
    bullets: ['ERP & CRM systems (SAP, Salesforce, etc.)', 'Cloud storage (S3, GCS, Azure Blob)', 'Data warehouses (Snowflake, Redshift)', 'IoT platforms and streaming sources', 'Custom REST / webhook integrations'],
  },
  {
    q: 'Is the platform secure?',
    a: 'Opticvance maintains enterprise-grade security, including SOC 2 Type II and ISO 27001 compliance, ensuring that your sensitive business data is fully protected.',
    bullets: ['SOC 2 Type II & ISO 27001 compliance', 'TLS in transit and AES-256 at rest', 'VPC peering & private endpoints', 'Enterprise access controls and audit logging'],
  },
  {
    q: 'Do I need a data science team to use it?',
    a: 'No. Opticvance’s no-code ML studio and intuitive dashboards allow non-technical users to generate insights, visualize data, and build predictive models without coding experience.',
    bullets: ['No-code ML studio', 'Pre-built templates and automations', 'Intuitive dashboards and visualizations', 'Advanced SDKs available for power users'],
  },
];

/* ── Highlight matching text ── */
const highlightText = (text, query) => {
  if (!query || query.length < 2) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-primary/25 text-white rounded px-0.5">{part}</mark>
    ) : (
      part
    )
  );
};

/* ── FAQ item ── */
const FAQItem = ({ item, index, isOpen, onToggle, searchQuery, isMatch }) => (
  <motion.div
    className={`rounded-xl overflow-hidden transition-shadow duration-300 ${
      isOpen ? 'glass-card shadow-lg shadow-primary/5' : 'glass-card'
    } ${searchQuery && !isMatch ? 'opacity-30' : 'opacity-100'}`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.06 }}
    layout
  >
    <button
      className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:text-primary transition-colors"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`faq-answer-${index}`}
    >
      <span className="font-semibold pr-4 text-sm sm:text-base">
        {highlightText(item.q, searchQuery)}
      </span>
      <motion.span
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
        className="flex-shrink-0 text-primary"
      >
        {icons.chevronDown}
      </motion.span>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          id={`faq-answer-${index}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
          role="region"
        >
          <div className="px-5 sm:px-6 pb-5 sm:pb-6">
            <p className="text-gray-400 leading-relaxed mb-3 text-sm sm:text-base">
              {highlightText(item.a, searchQuery)}
            </p>
            {item.bullets && (
              <ul className="space-y-2">
                {item.bullets.map((bullet, bi) => (
                  <li key={bi} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <span className="flex-shrink-0 mt-1 text-secondary">{icons.check}</span>
                    <span>{highlightText(bullet, searchQuery)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

/* ── Main ── */
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const matchIndices = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return new Set();
    const q = searchQuery.toLowerCase();
    const matches = new Set();
    faqItems.forEach((item, i) => {
      if (
        item.q.toLowerCase().includes(q) ||
        item.a.toLowerCase().includes(q) ||
        (item.bullets && item.bullets.some((b) => b.toLowerCase().includes(q)))
      ) {
        matches.add(i);
      }
    });
    return matches;
  }, [searchQuery]);

  // Auto-open first match
  const hasSearch = searchQuery.length >= 2;

  return (
    <Section id="faq" className="py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag="Predictive Decision Answers"
          title={<>Frequently Asked <span className="gradient-text">Questions</span></>}
          desc="Helping organizations unlock insights and act on data up-to-the-minute. Can't find an answer? Contact our team."
        />

        {/* Search input */}
        <div className="relative mb-8">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search questions..."
            className="w-full bg-dark-card border border-dark-border rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-primary/50 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
              aria-label="Clear search"
            >
              {icons.close}
            </button>
          )}
          {hasSearch && (
            <span className="absolute right-12 top-1/2 -translate-y-1/2 text-xs text-gray-500">
              {matchIndices.size} match{matchIndices.size !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        {/* FAQ items */}
        <div className="space-y-3" role="list">
          {faqItems.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              searchQuery={hasSearch ? searchQuery : ''}
              isMatch={!hasSearch || matchIndices.has(i)}
            />
          ))}
        </div>
      </div>
    </Section>
  );
};

export default FAQ;
